import { Button, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import { Modal } from 'antd';
import { AppProps } from 'presentation/App';
import { AlertModalProps } from 'presentation/components/Modal/Modal.type';
import { openErrorNoti } from 'presentation/components/Notification';
import { WebView } from 'presentation/components/WebView';
import { FormCheckBox } from 'presentation/components/form/FormCheckBox.component';
import { FormDatePicker } from 'presentation/components/form/FormDatePicker.component';
import { FormInput } from 'presentation/components/form/FormInput.component';
import { FormTextArea } from 'presentation/components/form/FormTextArea.component';
import {
    completeFetching,
    showAlert,
    startFetching,
} from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import React from 'react';
import { DATE_FORMAT } from 'utilities/constants/DATE';
import { PLACEHOLDER } from 'utilities/constants/PLACEHOLDER';
import ConfirmModal from 'presentation/components/ConfirmModal';
import { ConfirmModalComponent } from '../../CU-003-02/components/ConfirmModal';

export const FormRegistMember = (props: AppProps) => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [btnDisable, setBtnDisable] = React.useState<boolean>(true);
    const [ekycUrl, setEkycUrl] = React.useState<string>();
    const [ekycModal, setEkycModal] = React.useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = React.useState<boolean>(false);
    const [disableForm, setDisableForm] = React.useState<boolean>(false);
    
    const { userUseCase, userStorageUseCase } = props;

    React.useEffect(() => {
        (async () => {
            await getEkycInfo();
        })();
    }, []);

    const getEkycInfo = async () => {
        try {
            dispatch(startFetching());
            const response = await userUseCase.getBeforeEkycInfo();
            if (response?.result) {
                const birthDay = response?.result?.birthday
                    ? dayjs(response?.result?.birthday, DATE_FORMAT.FORMAT2)
                    : undefined;
                const formValues = {
                    ...response?.result,
                    birthday: birthDay,
                };
                if (formValues.step ?? 0 > 2) {
                    form.setFieldsValue(formValues);
                }
                setDisableForm(formValues.step == 3);
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const onChangeForm = () => {
        form.validateFields({
            validateOnly: true,
        })
            .then(() => {
                setBtnDisable(false);
            })
            .catch((err) => {
                setBtnDisable(err.errorFields.length > 0);
            });
    };

    const onSubmit = async (values: any) => {
        try {
            dispatch(startFetching());
            const payload = {
                memberName: values.memberName,
                memberNameKana: values.memberNameKana,
                postalCode: values.postalCode,
                address: values.address,
                birthday: dayjs(values.birthday).format(DATE_FORMAT.FORMAT2),
                cicTransferAgreement: values.cicTransferAgreement?.[0],
                kycAgreement: values.kycAgreement?.[0],
            };
            await userUseCase.registBeforeEkyc(payload);
            const userInfo = userStorageUseCase.getUserInfo();
            const response = await userUseCase.startEkyc({
                memberAccountId: userInfo?.user?.memberAccountId || 0,
            });
            if (response?.result?.result?.ekycUrl) {
                setEkycUrl(response?.result?.result?.ekycUrl);
                setShowConfirmModal(true);
            }
        } catch (error: any) {
            if (error.message) {
                const alertProps: AlertModalProps = {
                    message: error.message,
                    type: 'warning',
                    okText: 'OK',
                };
                dispatch(showAlert(alertProps));
            } else {
                openErrorNoti();
            }
        } finally {
            dispatch(completeFetching());
        }
    };
    return (
        // <div className="w-full xl:w-2/3 2xl:w-1/2">
        <div className="w-full">
            <ConfirmModalComponent
                open={showConfirmModal}
                onCancel={() => setShowConfirmModal(false)}
                onOk={() => {
                    setEkycModal(true);
                    setShowConfirmModal(false)
                }}
                title={""}
            />
            <WebView
                url={ekycUrl || ''}
                open={ekycModal}
                onClose={() => {
                    setEkycUrl(undefined);
                    setEkycModal(false);
                }}
            />
            <Form form={form} onValuesChange={onChangeForm} onFinish={onSubmit} className="w-full">
                <Row gutter={[24, 0]}>
                    <Col span={12}>
                        <FormInput
                            required
                            label="氏名"
                            size={'large'}
                            name="memberName"
                            maxLength={20}
                            labelClassName="content-step-form label-step-form"
                            disabled={disableForm}
                            placeholder={PLACEHOLDER.NAME}
                        />
                    </Col>
                    <Col span={12}>
                        <FormInput
                            required
                            label="氏名（カナ）"
                            size={'large'}
                            name="memberNameKana"
                            maxLength={20}
                            labelClassName="content-step-form label-step-form"
                            disabled={disableForm}
                            placeholder={PLACEHOLDER.NAME_KANA}
                        />
                    </Col>
                </Row>
                <Row gutter={[24, 0]}>
                    <Col span={12}>
                        <FormDatePicker
                            label="生年月日"
                            placeholder={PLACEHOLDER.SELECT}
                            required
                            name="birthday"
                            labelClassName="content-step-form label-step-form"
                            format={DATE_FORMAT.FORMAT4}
                            disabled={disableForm}
                        />
                    </Col>
                </Row>
                <Row gutter={[24, 0]}>
                    <Col span={12}>
                        <FormInput
                            required
                            type="postcode"
                            maxLength={7}
                            label="郵便番号（ハイフンなし）"
                            size={'large'}
                            name="postalCode"
                            labelClassName="content-step-form label-step-form"
                            disabled={disableForm}
                            placeholder={PLACEHOLDER.POSTCODE}
                        />
                    </Col>
                    <Col span={12}>
                        <FormInput
                            required
                            maxLength={100}
                            label="住所"
                            size={'large'}
                            name="address"
                            labelClassName="content-step-form label-step-form"
                            disabled={disableForm}
                            placeholder={PLACEHOLDER.ADDRESS}
                        />
                        <div>
                            ※  照会するID（免許証、マイナンバーカードなど）と同じ情報を入力してください<br/>
                            （マンション・アパート名など)
                        </div>
                    </Col>
                </Row>
                <Row gutter={[24, 0]}>
                    <Col span={24}>
                        <FormTextArea
                            label="反社会勢力"
                            name="antiSocialForce"
                            disabled
                            placeholder={PLACEHOLDER.ANTISOCIAL_TEXT}
                            size={'large'}
                            className="custom-bg"
                            labelClassName="content-step-form label-step-form"
                            rows={10}
                        />
                    </Col>
                </Row>
                <Row gutter={[24, 0]}>
                    <Col span={24}>
                        <FormCheckBox
                            name="kycAgreement"
                            defaultValue={1}
                            showErrorMessage={false}
                            required
                            options={[
                                {
                                    value: 1,
                                    label: '同意します。',
                                },
                            ]}
                            className="mb-0"
                            disabled={disableForm}
                        />
                    </Col>
                </Row>
                <Row gutter={[24, 0]} className="justify-center">
                    <Col span={12}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            style={{ width: '100%', height: 56, marginTop: 16 }}
                            disabled={btnDisable}
                        >
                            本人確認（eKYC外部サービス）に進む
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};
